// 09-10 (Pau aprobó la maqueta «mantener el dedo», iPad): el menú de un agente en la escena, al mantener el dedo (o
// con el clic derecho). Lo mismo que se hace desde su chat, sin tener que abrirlo: abrir, moverlo de mesa (abre
// «Ordenar» de su sala), fijarlo arriba y terminar su trabajo (abre el chat con su diálogo de cierre ya abierto: `cierrePedido` del store).
// Mismo aspecto que el menú de la cabecera del chat (chat/VentanaChat.tsx).
import * as Menu from '@radix-ui/react-dropdown-menu';
import { DoorOpen, LayoutGrid, MessageCircle, Pin, PinOff, type LucideIcon } from 'lucide-react';
import { useOficinaStore } from '../store';
import { t } from '../../../i18n';
import { useAppStore } from '../../../stores/app.store';
import { useToastStore } from '../../../stores/toast.store';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { quien } from '../marco/estado';

function Opcion({ icono: Icono, alElegir, children }: { icono: LucideIcon; alElegir: () => void; children: string }) {
  return (
    <Menu.Item
      onSelect={alElegir}
      // ≥ 44 px de alto: se pulsa con el dedo.
      className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
    >
      <Icono className="h-4 w-4 text-oficina-texto2" aria-hidden />
      {children}
    </Menu.Item>
  );
}

export function MenuAgente({
  c,
  alCerrar,
  alAbrir,
  alMover,
}: {
  c: ContratadoDTO;
  alCerrar: () => void;
  alAbrir: () => void;
  /** Sin ella (maquetas, sin ordenar) no se ofrece «Mover de mesa». */
  alMover?: () => void;
}) {
  const fijar = () => {
    void useAppStore
      .getState()
      .pinAgent(c.agentId, !c.fijado)
      .catch(() => useToastStore.getState().push({ message: t('oficinaV2.fijar.error') }));
  };
  return (
    <Menu.Root open onOpenChange={(abierto) => !abierto && alCerrar()}>
      {/* El ancla es la mesa entera: el menú sale encima (o debajo) de ella, nunca bajo el dedo — Radix elige la
          opción sobre la que se LEVANTA el dedo, y el dedo sigue sobre el agente al abrirse. */}
      <Menu.Trigger asChild>
        <span className="pointer-events-none absolute inset-0" aria-hidden tabIndex={-1} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={8}
          // El ancla no tiene texto: el menú se nombra a sí mismo (Radix lo nombraría por el ancla).
          aria-labelledby={undefined}
          aria-label={t('oficinaV2.escena.acciones', { nombre: quien(c) })}
          className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95"
        >
          <Opcion icono={MessageCircle} alElegir={alAbrir}>
            {t('oficinaV2.escena.abrirChat')}
          </Opcion>
          {alMover ? (
            <Opcion icono={LayoutGrid} alElegir={alMover}>
              {t('oficinaV2.mesas.moverDeMesa')}
            </Opcion>
          ) : null}
          <Opcion icono={c.fijado ? PinOff : Pin} alElegir={fijar}>
            {t(c.fijado ? 'oficinaV2.fijar.quitar' : 'oficinaV2.fijar.poner')}
          </Opcion>
          {c.ciclo === 'activo' ? (
            <Opcion
              icono={DoorOpen}
              alElegir={() => {
                useOficinaStore.getState().pedirCierre(c.agentId);
                alAbrir();
              }}
            >
              {t('oficinaV2.cierre.boton')}
            </Opcion>
          ) : null}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
